import { values, zipObj, map, toPairs } from 'ramda';

export const hexColorRegexp = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

/**
 * Converts hex to rgb
 */
export const hexToRgb = (hex: string): readonly [number, number, number] => {
  const result = hexColorRegexp.exec(hex);
  const rgb = result ? ([parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] as const) : null;

  return rgb;
};

/**
 * Converts component to hex
 *
 */
export const componentToHex = (component: number): string => {
  const hex = component.toString(16);

  return hex.length === 1 ? `0${hex}` : hex;
};

/**
 * Converts rgb to hex.
 *
 * @param  red red value
 * @param  green green value
 * @param  blue blue value
 * @returns hex value
 */
export const rgbToHex = (red: number, green: number, blue: number): string =>
  `#${componentToHex(red)}${componentToHex(green)}${componentToHex(blue)}`;

/**
 * Converts color to background color.
 */
export const toBackgroundColor = (hexColor: string): string =>
  hexColor === '#000000'
    ? '#FFFFFF'
    : rgbToHex(...(hexToRgb(hexColor).map((rgb) => Math.round(rgb * 0.25 + 255 * 0.75)) as [number, number, number])); // eslint-disable-line

/**
 * Codecolors
 */
const colors = {
  grey: '#1B1616',
  greyMedium: '#6c6c6c',
  greyLight: '#aaaaaa',
  brown: '#7A5649',
  brownLight: '#bd8976',
  redLight: '#ee695d',
  redDark: '#6d1109',
  pink: '#EC2364',
  redMedium: '#a41508',
  red: '#F21F0D',
  tan: '#FFC197',
  deepOrange: '#FF5C27',
  orange: '#FFA727',
  yellow: '#ffdb27',
  yellowDark: '#9e8400',
  yellowMedium: '#cfb11b',
  lime: '#CDDC39',
  lightGreen: '#8BC34A',
  green: '#46B04F',
  greenGrey: '#698B60',
  greenDark: '#465c40',
  blueDark: '#455a65',
  cyanDark: '#0c525c',
  teal: '#009687',
  cyan: '#00BBD4',
  lightBlue: '#76C8EE',
  tealLight: '#28d9c8',
  blue: '#0A93F3',
  indigoDark: '#1f2a71',
  indigo: '#3D4DB5',
  deepPurple: '#6834B7',
  deepPurpleMedium: '#9d62f7',
  deepPurpleLight: '#c6a1fe',
  purpleMedium: '#71197e',
  purple: '#9E22B0',
  pinkDark: '#a41a47',
  purpleGrey: '#84608B',
  // blueGrey: '#607D8B',
} as const;

// Calculate the background colors for ATLAS.ti desktop export
const backgroundColors = {};

for (const color in colors) {
  if (Object.prototype.hasOwnProperty.call(colors, color)) {
    backgroundColors[color] = toBackgroundColor(colors[color]);
  }
}

export default colors;

/**
 * Color values.
 */
export const colorValues = values(colors);

const colorKeys = Object.keys(colors);

/**
 * Returns a random code color.
 */
export const randomColor = (): string => colors[colorKeys[(colorKeys.length * Math.random()) << 0]]; // eslint-disable-line

/**
 * Color array.
 */
export const colorArray = map((color) => {
  const colorObj = zipObj(['name', 'rgb', 'rgbBg'], color);
  colorObj.rgbBg = backgroundColors[color[0]];

  return colorObj;
}, toPairs<string>(colors));
